import { Box, CircularProgress, Dialog, DialogContent, IconButton, Typography } from '@mui/material';
import { canPreviewFile, getFileIcon, getFileIconColor, isAudioFile, isPdfFile, isVideoFile } from '@utils/fileHelpers';
import { FC, useEffect, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';

import ScrollArea from './ScrollArea';

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

interface FileItem {
    _id: string;
    name: string;
    url: string;
    thumbnailUrl?: string;
    mimetype?: string;
}

interface FilePreviewDialogProps {
    open: boolean;
    onClose: () => void;
    file: {
        name: string;
        url: string;
    };
    onDownload?: () => void;
    showDownloadButton?: boolean;
    numPages?: number;
    onDocumentLoadSuccess?: (params: { numPages: number }) => void;
    files?: FileItem[];
    currentFileId?: string;
    onNavigate?: (file: FileItem) => void;
    hasMore?: boolean;
    onLoadMore?: () => void;
    loading?: boolean;
}

const FilePreviewDialog: FC<FilePreviewDialogProps> = ({
    open,
    onClose,
    file,
    onDownload,
    showDownloadButton = true,
    numPages = 0,
    onDocumentLoadSuccess,
    files = [],
    currentFileId,
    onNavigate,
    hasMore = false,
    onLoadMore,
    loading = false,
}) => {
    const isPdf = isPdfFile(undefined, file.name);
    const thumbnailContainerRef = useRef<HTMLDivElement>(null);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [blobUrl, setBlobUrl] = useState<string | null>(null);
    const [blobLoading, setBlobLoading] = useState(false);

    useEffect(() => {
        if (!isPdf || !file.url) {
            setBlobUrl(prev => {
                if (prev) URL.revokeObjectURL(prev);
                return null;
            });
            return;
        }
        let cancelled = false;
        setBlobLoading(true);
        setPdfLoading(true);
        fetch(file.url)
            .then(res => {
                if (!res.ok) throw new Error('fetch failed');
                return res.blob();
            })
            .then(blob => {
                if (cancelled) return;
                const url = URL.createObjectURL(blob);
                setBlobUrl(prev => {
                    if (prev) URL.revokeObjectURL(prev);
                    return url;
                });
            })
            .catch(() => {
                if (!cancelled) setBlobUrl(null);
            })
            .finally(() => {
                if (!cancelled) setBlobLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [file.url, isPdf]);

    const handleDocumentLoadSuccess = (params: { numPages: number }) => {
        setPdfLoading(false);
        onDocumentLoadSuccess?.(params);
    };

    const handleNavigatePrevious = () => {
        if (!currentFileId || files.length === 0) return;
        const currentIndex = files.findIndex(f => f._id === currentFileId);
        if (currentIndex > 0) {
            const prevFile = files[currentIndex - 1];
            if (canPreviewFile(prevFile.mimetype, prevFile.name)) {
                onNavigate?.(prevFile);
            }
        }
    };

    const handleNavigateNext = () => {
        if (!currentFileId || files.length === 0) return;
        const currentIndex = files.findIndex(f => f._id === currentFileId);
        if (currentIndex < files.length - 1) {
            const nextFile = files[currentIndex + 1];
            if (canPreviewFile(nextFile.mimetype, nextFile.name)) {
                onNavigate?.(nextFile);
            }
        }
    };

    const currentIndex = files.findIndex(f => f._id === currentFileId);
    const hasPrevious = currentIndex > 0;
    const hasNext = currentIndex < files.length - 1;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullScreen
            scroll='paper'
            PaperProps={{
                sx: {
                    backgroundColor: '#1a1a1a',
                    borderRadius: '12px',
                    overflow: 'hidden',
                },
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '16px 24px',
                    backgroundColor: '#1a1a1a',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                }}
            >
                <Typography
                    sx={{
                        fontFamily: 'Plus Jakarta Sans',
                        fontSize: '14px',
                        fontWeight: 500,
                        color: '#fff',
                    }}
                >
                    {files.length > 0 ? `${files.findIndex(f => f._id === currentFileId) + 1}/${files.length}` : ''}
                </Typography>
                <Typography
                    sx={{
                        fontFamily: 'Plus Jakarta Sans',
                        fontSize: '18px',
                        fontWeight: 600,
                        color: '#fff',
                        flex: 1,
                        textAlign: 'center',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        mx: 2,
                    }}
                >
                    Preview File: {file.name}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    {showDownloadButton && onDownload && (
                        <IconButton
                            onClick={onDownload}
                            sx={{
                                color: '#fff',
                                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                            }}
                        >
                            <i className="fi fi-br-download" style={{ fontSize: '20px' }} />
                        </IconButton>
                    )}
                    <IconButton
                        onClick={onClose}
                        sx={{
                            color: '#fff',
                            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                        }}
                    >
                        <i className="fi fi-br-cross" style={{ fontSize: '20px' }} />
                    </IconButton>
                </Box>
            </Box>
            {/* Content Area */}
            <DialogContent
                dividers
                style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative' }}
                sx={{
                    backgroundColor: '#2a2a2a',
                    paddingTop: 3,
                    '& .react-pdf__Page': {
                        paddingBottom: 3
                    },
                }}
            >
                {file.url &&
                    (isPdf ? (
                        <Box sx={{
                            flex: 1,
                            height: '100%',
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                        }}
                            key={file.url}
                        >
                            {blobLoading ? (
                                <CircularProgress sx={{ color: '#fff' }} />
                            ) : (
                            <Box
                                sx={{
                                    flex: 1,
                                    height: '100%',
                                    gap: 2,
                                }}
                                file={blobUrl}
                                onLoadSuccess={handleDocumentLoadSuccess}
                                loading={() => <CircularProgress sx={{ color: '#fff' }} />}
                                component={Document}
                            >
                                {Array.from({ length: numPages }, (_, index) => (
                                    <Box
                                        key={`page_${index + 1}`}
                                        sx={{
                                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                                            borderRadius: '4px',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        <Page
                                            pageNumber={index + 1}
                                            width={Math.min(window.innerWidth * 0.7, 900)}
                                        />
                                    </Box>
                                ))}
                            </Box>
                            )}
                        </Box>
                    ) : isVideoFile(undefined, file.name) ? (
                        <Box
                            sx={{
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <video
                                controls
                                autoPlay
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: '100%',
                                    objectFit: 'contain',
                                }}
                                src={file.url}
                            >
                                <source src={file.url} />
                                Your browser does not support the video tag.
                            </video>
                        </Box>
                    ) : isAudioFile(undefined, file.name) ? (
                        <Box
                            sx={{
                                width: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 3,
                            }}
                        >
                            <Box
                                sx={{
                                    width: '200px',
                                    height: '200px',
                                    borderRadius: '16px',
                                    backgroundColor: 'rgba(84, 71, 222, 0.1)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <i className="fi fi-br-file-music" style={{ fontSize: '80px', color: '#5447DE' }} />
                            </Box>
                            <Typography
                                sx={{
                                    fontFamily: 'Plus Jakarta Sans',
                                    fontSize: '16px',
                                    fontWeight: 600,
                                    color: '#fff',
                                    textAlign: 'center',
                                    maxWidth: '80%',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {file.name}
                            </Typography>
                            <audio
                                controls
                                autoPlay
                                style={{
                                    width: '100%',
                                    maxWidth: '600px',
                                }}
                                src={file.url}
                            >
                                <source src={file.url} />
                                Your browser does not support the audio tag.
                            </audio>
                        </Box>
                    ) : (
                        <img
                            src={file.url}
                            alt={file.name}
                            style={{
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain',
                            }}
                        />
                    ))}
            </DialogContent>

            {/* Left Navigation Button */}
            {files.length > 0 && hasPrevious && (
                <IconButton
                    onClick={handleNavigatePrevious}
                    sx={{
                        position: 'absolute',
                        left: '16px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 10,
                        backgroundColor: 'rgba(0, 0, 0, 0.6)',
                        color: '#fff',
                        width: '48px',
                        height: '48px',
                        '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        },
                    }}
                >
                    <i className="fi fi-br-angle-left" style={{ fontSize: '24px' }} />
                </IconButton>
            )}

            {/* Right Navigation Button */}
            {files.length > 0 && hasNext && (
                <IconButton
                    onClick={handleNavigateNext}
                    sx={{
                        position: 'absolute',
                        right: '16px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 10,
                        backgroundColor: 'rgba(0, 0, 0, 0.6)',
                        color: '#fff',
                        width: '48px',
                        height: '48px',
                        '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        },
                    }}
                >
                    <i className="fi fi-br-angle-right" style={{ fontSize: '24px' }} />
                </IconButton>
            )}

            {/* Thumbnail Navigation */}
            {files.length > 0 && (
                <Box
                    sx={{
                        backgroundColor: '#1a1a1a',
                        padding: '16px 24px',
                        borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                    }}
                >
                    <ScrollArea
                        hasMore={hasMore}
                        loading={loading}
                        onLoadMore={onLoadMore || (() => { })}
                        orientation="horizontal"
                        sx={{
                            gap: '12px',
                            paddingBottom: '4px',
                            '&::-webkit-scrollbar': {
                                height: '8px',
                            },
                            '&::-webkit-scrollbar-track': {
                                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                borderRadius: '4px',
                            },
                            '&::-webkit-scrollbar-thumb': {
                                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                borderRadius: '4px',
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                                },
                            },
                        }}
                    >
                        {files.map((fileItem) => {
                            const isActive = fileItem._id === currentFileId;
                            const canPreview = canPreviewFile(fileItem.mimetype, fileItem.name);
                            const isImage = fileItem.mimetype?.includes('image');
                            const isVideo = fileItem.mimetype?.includes('video');

                            return (
                                <Box
                                    key={fileItem._id}
                                    onClick={() => canPreview && onNavigate?.(fileItem)}
                                    sx={{
                                        minWidth: '100px',
                                        width: '100px',
                                        height: '120px',
                                        borderRadius: '8px',
                                        overflow: 'hidden',
                                        cursor: canPreview ? 'pointer' : 'default',
                                        opacity: canPreview ? 1 : 0.4,
                                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        position: 'relative', border: isActive ? '2px solid #5447DE' : '2px solid transparent',
                                        '&:hover': canPreview ? {
                                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
                                            border: '2px solid rgba(84, 71, 222, 0.6)',
                                        } : {},
                                    }}
                                >
                                    {isImage ? (
                                        <Box
                                            component="img"
                                            src={fileItem.thumbnailUrl || fileItem.url}
                                            alt={fileItem.name}
                                            sx={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                            }}
                                        />
                                    ) : isVideo ? (
                                        <>
                                            <video
                                                width="100%"
                                                height="100%"
                                                style={{
                                                    backgroundColor: 'rgba(0, 0, 0, 1)',
                                                    borderRadius: '8px',
                                                    objectFit: 'cover',
                                                }}>
                                                <source src={fileItem.url} type={fileItem.mimetype} />
                                            </video>
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    top: '50%',
                                                    left: '50%',
                                                    transform: 'translate(-50%, -50%)',
                                                    backgroundColor: 'rgba(0, 0, 0, 0)',
                                                    borderRadius: '50%',
                                                    width: '32px',
                                                    height: '32px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        backgroundColor: 'white',
                                                        borderRadius: '50%',
                                                        width: '100%',
                                                        height: '100%',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                    }}
                                                >
                                                    <i className="fi fi-br-play" style={{ fontSize: '12px', color: '#000', marginLeft: '2px' }} />
                                                </Box>
                                            </Box>
                                        </>
                                    ) : (
                                        <Box
                                            component="i"
                                            className={getFileIcon(fileItem.mimetype, fileItem.name)}
                                            sx={{
                                                fontSize: '32px',
                                                color: getFileIconColor(fileItem.mimetype, fileItem.name),
                                            }}
                                        />
                                    )}
                                </Box>
                            );
                        })}
                    </ScrollArea>
                </Box>
            )}
        </Dialog>
    );
};

export default FilePreviewDialog;
